import mixpanel from 'mixpanel-browser';

import type { AnalyticsEvent } from '@/analytics/events';

const isDebug = process.env.REACT_APP_MIXPANEL_DEBUG;

/** Hostname of the production webapp; anything else (dev site, localhost) is 'dev'. */
const PROD_HOSTNAME = 'app.simplesubwater.com';

/**
 * Percentage of production sessions to record for Mixpanel Session Replay.
 * Sized to stay under the plan's 10k replays/month allowance at current traffic
 * (~90-100 prod users/day); raise once the Session Replay page shows actual volume.
 */
const PROD_SESSION_REPLAY_PERCENT = 50;

/**
 * Elements whose text stays readable in Session Replay. Everything else is masked.
 * Matching walks up the DOM, so a selector unmasks the whole subtree beneath it.
 * Only add selectors whose content is hard-coded UI text, never customer data:
 * - MuiTab-root: tab labels (Billing, Usage, Devices, ...)
 * - MuiButton-root: button labels
 * - MuiTableCell-head: column headers
 * - mp-unmask: opt-in marker for static items that share a class with customer
 *   data, e.g. Home and Add Property in the drawer next to property names
 */
const SESSION_REPLAY_UNMASK_SELECTORS = [
  '.MuiTab-root',
  '.MuiButton-root',
  '.MuiTableCell-head',
  '.mp-unmask',
];

let initialized = false;

/**
 * Initialize Mixpanel once at app startup (called from index.tsx).
 *
 * Registers super properties sent with every event:
 * - environment: 'prod' | 'dev', so reports can filter environments without URL matching
 * - app_version: the release tag baked in at build time (git describe in the deploy workflows)
 * - source: 'user', distinguishing human actions from backend-sent events
 *   (the device-lambdas Mixpanel client marks its events with $os: 'backend')
 *
 * Session Replay records a sample of production sessions only. Dev and localhost
 * sessions share the same Mixpanel project token, so recording them would consume
 * the plan's monthly replay allowance without telling us anything about real users.
 * All text and inputs are masked by default. Only the static UI chrome listed in
 * SESSION_REPLAY_UNMASK_SELECTORS is readable, so a replay shows which tab, button,
 * or column the user was on without exposing tenant names or dollar amounts.
 *
 * Super property keys (environment, app_version, source) are reserved: a per-event
 * property with the same key silently overwrites the super property on that event.
 */
export const initMixpanel = (): void => {
  if (initialized) return;
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;

  if (!token) {
    console.error('Mixpanel token is missing.');
    return;
  }

  const isProd = window.location.hostname === PROD_HOSTNAME;

  mixpanel.init(token, {
    debug: isDebug === 'true' ? true : false,
    track_pageview: false,
    persistence: 'localStorage',
    // utm_* on the landing URL (e.g. leak-alert email links) should tag that visit's
    // events only, not stick to every later event for the life of localStorage.
    stop_utm_persistence: true,
    record_sessions_percent: isProd ? PROD_SESSION_REPLAY_PERCENT : 0,
    record_heatmap_data: true,
    record_mask_all_text: true,
    record_unmask_text_selector: SESSION_REPLAY_UNMASK_SELECTORS,
    // The SDK captures browser console output into replays by default; the app
    // logger prints request payloads, which would bypass the text masking above.
    record_console: false,
  });
  mixpanel.register({
    environment: isProd ? 'prod' : 'dev',
    app_version: process.env.REACT_APP_VERSION || 'unknown',
    source: 'user',
  });
  initialized = true;
};

const useMixPanel = () => {
  const trackEvent = (eventName: AnalyticsEvent, properties: Record<string, any> = {}) => {
    // Don't track anything if Mixpanel hasn't been initialized properly
    if (!initialized || !mixpanel?.track) return;
    mixpanel.track(eventName, properties);
  };

  const setUser = (userId: string, properties: Record<string, unknown> = {}) => {
    if (!initialized || !mixpanel?.identify) return;
    mixpanel.identify(userId);
    mixpanel.people?.set?.(properties);
  };

  /** Set profile properties on the current user without re-identifying. */
  const setUserProperties = (properties: Record<string, unknown>) => {
    if (!initialized) return;
    mixpanel.people?.set?.(properties);
  };

  return { trackEvent, setUser, setUserProperties };
};

export default useMixPanel;
